import { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import type { ShoppingItem, ShoppingCategory } from '@homeapp/shared';

interface UseShoppingReturn {
  items: ShoppingItem[];
  categories: ShoppingCategory[];
  loading: boolean;
  error: string | null;
  addItem: (name: string, category: string) => Promise<void>;
  toggleItem: (itemId: string, currentStatus: string) => Promise<void>;
  deleteItem: (itemId: string) => Promise<void>;
  addCategory: (name: string, icon?: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useShopping(): UseShoppingReturn {
  const [items, setItems] = useState<ShoppingItem[]>([]);
  const [categories, setCategories] = useState<ShoppingCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    try {
      setError(null);
      const [activeRes, boughtRes, catRes] = await Promise.all([
        api.get<{ items: ShoppingItem[] }>('/shopping/items?status=active'),
        api.get<{ items: ShoppingItem[] }>('/shopping/items?status=bought'),
        api.get<{ categories: ShoppingCategory[] }>('/shopping/categories'),
      ]);
      setItems([...activeRes.items, ...boughtRes.items]);
      setCategories(catRes.categories);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const addItem = async (name: string, category: string) => {
    const res = await api.post<{ item: ShoppingItem }>('/shopping/items', { name, category });
    setItems(prev => [res.item, ...prev]);
  };

  const toggleItem = async (itemId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'bought' : 'active';
    // Optimistic update
    setItems(prev => prev.map(i =>
      i.itemId === itemId ? { ...i, status: newStatus as 'active' | 'bought' } : i
    ));
    try {
      await api.put(`/shopping/items/${itemId}`, { status: newStatus });
      await fetchAll(); // Refresh to get accurate server state
    } catch {
      await fetchAll(); // Revert on error
    }
  };

  const deleteItem = async (itemId: string) => {
    setItems(prev => prev.filter(i => i.itemId !== itemId));
    try {
      await api.delete(`/shopping/items/${itemId}`);
    } catch {
      await fetchAll();
    }
  };

  const addCategory = async (name: string, icon?: string) => {
    const res = await api.post<{ category: ShoppingCategory }>('/shopping/categories', { name, icon });
    setCategories(prev => [...prev, res.category]);
  };

  return { items, categories, loading, error, addItem, toggleItem, deleteItem, addCategory, refresh: fetchAll };
}
